/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode", "ObjectPropertyName")
@file:KlangScript.Library("sprudel")

package io.peekandpoke.klang.sprudel.lang

import io.peekandpoke.klang.audio_bridge.SoundValue
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel._liftOrReinterpretStringField

// -- sndPluck() -------------------------------------------------------------------------------------------------------

private fun applySndPluck(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("pluck")) }

/**
 * Sets the sound to a Karplus-Strong plucked string and optionally configures its
 * parameters. Each parameter is independent and patternable.
 *
 * Each field is optional; trailing fields can be omitted.
 * - **feedback**: how long the string rings, 0.9 (a short thud) to 0.999 (rings for seconds)
 * - **brightness**: lowpass cutoff, 0.0 (dark) to 1.0 (bright)
 * - **pickPosition**: pluck position, 0.0 (bridge) to 1.0 (neck)
 * - **stiffness**: harmonic stiffness, 0.0 (nylon) to 1.0 (piano wire)
 *
 * ```KlangScript(Playable)
 * note("c3 e3 g3").sndPluck()                     // default plucked string
 * note("c3 e3 g3").sndPluck(0.999, 0.8)          // bright, long sustain
 * note("c3 e3 g3").sndPluck(0.93, 0.2)           // dark pizzicato
 * note("c3 e3 g3").sndPluck(0.996, 0.5, 0.2, 0.5)  // steel string, bridge pick
 * ```
 *
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 * @tool SprudelPluckEditor
 * @tool feedback: SprudelPluckEditor, SprudelPluckSequenceEditor
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time
 * @return A new pattern with sound set to "pluck" and parameters applied.
 * @category tonal
 * @tags pluck, string, karplus-strong, physical-model, snd
 */
@KlangScript.Function
fun SprudelPattern.sndPluck(
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    var p = applySndPluck(this)
    if (feedback != null) p = p.ignitorParam("feedback", feedback, callInfo?.forParam(0, 1))
    if (brightness != null) p = p.ignitorParam("brightness", brightness, callInfo?.forParam(1, 1))
    if (pickPosition != null) p = p.ignitorParam("pickPosition", pickPosition, callInfo?.forParam(2, 1))
    if (stiffness != null) p = p.ignitorParam("stiffness", stiffness, callInfo?.forParam(3, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to plucked string.
 *
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 * @return A new pattern with sound set to "pluck" and parameters applied.
 * @category tonal
 * @tags pluck, string, karplus-strong, physical-model, snd
 */
@KlangScript.Function
fun String.sndPluck(
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndPluck(feedback, brightness, pickPosition, stiffness, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to plucked string.
 *
 * ```KlangScript(Playable)
 * note("c3 e3 g3").apply(sndPluck(0.999, 0.8))
 * ```
 *
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 * @return A [PatternMapperFn] that sets sound to "pluck".
 * @category tonal
 * @tags pluck, string, karplus-strong, physical-model, snd
 */
@KlangScript.Function
fun sndPluck(
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    { p -> p.sndPluck(feedback, brightness, pickPosition, stiffness, callInfo) }

/**
 * Chains a plucked string sound onto this [PatternMapperFn].
 *
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 */
@KlangScript.Function
fun PatternMapperFn.sndPluck(
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.sndPluck(feedback, brightness, pickPosition, stiffness, callInfo) }

// -- sndSuperPluck() --------------------------------------------------------------------------------------------------

private fun applySndSuperPluck(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("superpluck")) }

/**
 * Sets the sound to a super plucked string (multiple detuned Karplus-Strong strings)
 * and optionally configures parameters via `"voices:detune:feedback:brightness:pickPosition:stiffness"`.
 *
 * Like a 12-string guitar or chorus of harps — each string has independent noise excitation
 * and drift, creating rich evolving shimmer.
 *
 * ```KlangScript(Playable)
 * note("c3 e3 g3").sndSuperPluck()                         // default 5-string
 * note("c3 e3 g3").sndSuperPluck(7, 0.3, 0.998, 0.8)       // 7-string, wide, bright, long
 * note("c3 e3 g3").sndSuperPluck(3, 0.1, 0.93, 0.2)        // 3-string, tight, dark pizzicato
 * ```
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 * @tool SprudelSuperPluckEditor
 * @tool voices: SprudelSuperPluckEditor, SprudelSuperPluckSequenceEditor
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time
 * @return A new pattern with sound set to "superpluck" and parameters applied.
 * @category tonal
 * @tags superpluck, pluck, string, karplus-strong, unison, physical-model, snd
 */
@KlangScript.Function
fun SprudelPattern.sndSuperPluck(
    voices: PatternLike? = null,
    spread: PatternLike? = null,
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    var p = applySndSuperPluck(this)
    if (voices != null) p = p.ignitorParam("voices", voices, callInfo?.forParam(0, 1))
    if (spread != null) p = p.ignitorParam("spread", spread, callInfo?.forParam(1, 1))
    if (feedback != null) p = p.ignitorParam("feedback", feedback, callInfo?.forParam(2, 1))
    if (brightness != null) p = p.ignitorParam("brightness", brightness, callInfo?.forParam(3, 1))
    if (pickPosition != null) p = p.ignitorParam("pickPosition", pickPosition, callInfo?.forParam(4, 1))
    if (stiffness != null) p = p.ignitorParam("stiffness", stiffness, callInfo?.forParam(5, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to super plucked string.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 * @return A new pattern with sound set to "superpluck".
 * @category tonal
 * @tags superpluck, pluck, string, karplus-strong, unison, physical-model, snd
 */
@KlangScript.Function
fun String.sndSuperPluck(
    voices: PatternLike? = null,
    spread: PatternLike? = null,
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndSuperPluck(voices, spread, feedback, brightness, pickPosition, stiffness, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to super plucked string.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 * @return A [PatternMapperFn] that sets sound to "superpluck".
 * @category tonal
 * @tags superpluck, pluck, string, karplus-strong, unison, physical-model, snd
 */
@KlangScript.Function
fun sndSuperPluck(
    voices: PatternLike? = null,
    spread: PatternLike? = null,
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    { p -> p.sndSuperPluck(voices, spread, feedback, brightness, pickPosition, stiffness, callInfo) }

/**
 * Chains a super plucked string sound onto this [PatternMapperFn].
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @param feedback How long the string rings: the share of its level kept on each pass round the loop, 0.9 (a short thud) to 0.999 (rings for seconds); a coefficient, not a time.
 * @param brightness Lowpass cutoff (0 = dark, 1 = bright).
 * @param pickPosition Pluck position (0 = bridge, 1 = neck).
 * @param stiffness String stiffness (0 = nylon, 1 = piano wire).
 */
@KlangScript.Function
fun PatternMapperFn.sndSuperPluck(
    voices: PatternLike? = null,
    spread: PatternLike? = null,
    feedback: PatternLike? = null,
    brightness: PatternLike? = null,
    pickPosition: PatternLike? = null,
    stiffness: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.sndSuperPluck(voices, spread, feedback, brightness, pickPosition, stiffness, callInfo) }

// -- sndSuperSaw() ----------------------------------------------------------------------------------------------------

private fun applySndSuperSaw(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("supersaw")) }

/**
 * Sets the sound to a super sawtooth (multiple detuned sawtooth oscillators).
 *
 * @param spread Detune spread between voices.
 * @tool SprudelSuperSawEditor
 * @tool voices: SprudelSuperSawEditor, SprudelSuperSawSequenceEditor
 * @param voices Number of oscillators (1–16)
 * @return A new pattern with sound set to "supersaw" and parameters applied.
 * @category tonal
 * @tags supersaw, saw, unison, oscillator, snd
 */
@KlangScript.Function
fun SprudelPattern.sndSuperSaw(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern {
    var p = applySndSuperSaw(this)
    if (voices != null) p = p.ignitorParam("voices", voices, callInfo?.forParam(0, 1))
    if (spread != null) p = p.ignitorParam("spread", spread, callInfo?.forParam(1, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to super sawtooth.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A new pattern with sound set to "supersaw".
 * @category tonal
 * @tags supersaw, saw, unison, oscillator, snd
 */
@KlangScript.Function
fun String.sndSuperSaw(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndSuperSaw(voices, spread, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to super sawtooth.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A [PatternMapperFn] that sets sound to "supersaw".
 * @category tonal
 * @tags supersaw, saw, unison, oscillator, snd
 */
@KlangScript.Function
fun sndSuperSaw(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    { p -> p.sndSuperSaw(voices, spread, callInfo) }

/**
 * Chains a super sawtooth sound onto this [PatternMapperFn].
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 */
@KlangScript.Function
fun PatternMapperFn.sndSuperSaw(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.sndSuperSaw(voices, spread, callInfo) }

// -- sndSuperSine() ---------------------------------------------------------------------------------------------------

private fun applySndSuperSine(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("supersine")) }

/**
 * Sets the sound to a super sine (multiple detuned sine oscillators).
 *
 * @param spread Detune spread between voices.
 * @tool SprudelSuperSawEditor
 * @tool voices: SprudelSuperSawEditor, SprudelSuperSawSequenceEditor
 * @param voices Number of oscillators (1–16)
 * @return A new pattern with sound set to "supersine" and parameters applied.
 * @category tonal
 * @tags supersine, sine, unison, oscillator, snd
 */
@KlangScript.Function
fun SprudelPattern.sndSuperSine(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern {
    var p = applySndSuperSine(this)
    if (voices != null) p = p.ignitorParam("voices", voices, callInfo?.forParam(0, 1))
    if (spread != null) p = p.ignitorParam("spread", spread, callInfo?.forParam(1, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to super sine.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A new pattern with sound set to "supersine".
 * @category tonal
 * @tags supersine, sine, unison, oscillator, snd
 */
@KlangScript.Function
fun String.sndSuperSine(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndSuperSine(voices, spread, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to super sine.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A [PatternMapperFn] that sets sound to "supersine".
 * @category tonal
 * @tags supersine, sine, unison, oscillator, snd
 */
@KlangScript.Function
fun sndSuperSine(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    { p -> p.sndSuperSine(voices, spread, callInfo) }

/**
 * Chains a super sine sound onto this [PatternMapperFn].
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 */
@KlangScript.Function
fun PatternMapperFn.sndSuperSine(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.sndSuperSine(voices, spread, callInfo) }

// -- sndSuperSquare() -------------------------------------------------------------------------------------------------

private fun applySndSuperSquare(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("supersquare")) }

/**
 * Sets the sound to a super square (multiple detuned square oscillators).
 *
 * @param spread Detune spread between voices.
 * @tool SprudelSuperSawEditor
 * @tool voices: SprudelSuperSawEditor, SprudelSuperSawSequenceEditor
 * @param voices Number of oscillators (1–16)
 * @return A new pattern with sound set to "supersquare" and parameters applied.
 * @category tonal
 * @tags supersquare, square, unison, oscillator, snd
 */
@KlangScript.Function
fun SprudelPattern.sndSuperSquare(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern {
    var p = applySndSuperSquare(this)
    if (voices != null) p = p.ignitorParam("voices", voices, callInfo?.forParam(0, 1))
    if (spread != null) p = p.ignitorParam("spread", spread, callInfo?.forParam(1, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to super square.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A new pattern with sound set to "supersquare".
 * @category tonal
 * @tags supersquare, square, unison, oscillator, snd
 */
@KlangScript.Function
fun String.sndSuperSquare(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndSuperSquare(voices, spread, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to super square.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A [PatternMapperFn] that sets sound to "supersquare".
 * @category tonal
 * @tags supersquare, square, unison, oscillator, snd
 */
@KlangScript.Function
fun sndSuperSquare(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    { p -> p.sndSuperSquare(voices, spread, callInfo) }

/**
 * Chains a super square sound onto this [PatternMapperFn].
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 */
@KlangScript.Function
fun PatternMapperFn.sndSuperSquare(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.sndSuperSquare(voices, spread, callInfo) }

// -- sndSuperTri() ----------------------------------------------------------------------------------------------------

private fun applySndSuperTri(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("supertri")) }

/**
 * Sets the sound to a super triangle (multiple detuned triangle oscillators).
 *
 * @param spread Detune spread between voices.
 * @tool SprudelSuperSawEditor
 * @tool voices: SprudelSuperSawEditor, SprudelSuperSawSequenceEditor
 * @param voices Number of oscillators (1–16)
 * @return A new pattern with sound set to "supertri" and parameters applied.
 * @category tonal
 * @tags supertri, triangle, unison, oscillator, snd
 */
@KlangScript.Function
fun SprudelPattern.sndSuperTri(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern {
    var p = applySndSuperTri(this)
    if (voices != null) p = p.ignitorParam("voices", voices, callInfo?.forParam(0, 1))
    if (spread != null) p = p.ignitorParam("spread", spread, callInfo?.forParam(1, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to super triangle.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A new pattern with sound set to "supertri".
 * @category tonal
 * @tags supertri, triangle, unison, oscillator, snd
 */
@KlangScript.Function
fun String.sndSuperTri(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndSuperTri(voices, spread, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to super triangle.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A [PatternMapperFn] that sets sound to "supertri".
 * @category tonal
 * @tags supertri, triangle, unison, oscillator, snd
 */
@KlangScript.Function
fun sndSuperTri(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    { p -> p.sndSuperTri(voices, spread, callInfo) }

/**
 * Chains a super triangle sound onto this [PatternMapperFn].
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 */
@KlangScript.Function
fun PatternMapperFn.sndSuperTri(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.sndSuperTri(voices, spread, callInfo) }

// -- sndSuperRamp() ---------------------------------------------------------------------------------------------------

private fun applySndSuperRamp(source: SprudelPattern): SprudelPattern =
    source._liftOrReinterpretStringField(emptyList()) { copy(sound = SoundValue.Named("superramp")) }

/**
 * Sets the sound to a super ramp (multiple detuned ramp oscillators).
 *
 * @param spread Detune spread between voices.
 * @tool SprudelSuperSawEditor
 * @tool voices: SprudelSuperSawEditor, SprudelSuperSawSequenceEditor
 * @param voices Number of oscillators (1–16)
 * @return A new pattern with sound set to "superramp" and parameters applied.
 * @category tonal
 * @tags superramp, ramp, unison, oscillator, snd
 */
@KlangScript.Function
fun SprudelPattern.sndSuperRamp(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern {
    var p = applySndSuperRamp(this)
    if (voices != null) p = p.ignitorParam("voices", voices, callInfo?.forParam(0, 1))
    if (spread != null) p = p.ignitorParam("spread", spread, callInfo?.forParam(1, 1))
    return p
}

/**
 * Parses this string as a pattern and sets sound to super ramp.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A new pattern with sound set to "superramp".
 * @category tonal
 * @tags superramp, ramp, unison, oscillator, snd
 */
@KlangScript.Function
fun String.sndSuperRamp(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).sndSuperRamp(voices, spread, callInfo)

/**
 * Returns a [PatternMapperFn] that sets the sound to super ramp.
 *
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 * @return A [PatternMapperFn] that sets sound to "superramp".
 * @category tonal
 * @tags superramp, ramp, unison, oscillator, snd
 */
@KlangScript.Function
fun sndSuperRamp(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    { p -> p.sndSuperRamp(voices, spread, callInfo) }

/**
 * Chains a super ramp sound onto this [PatternMapperFn].
 * @param voices Number of unison voices.
 * @param spread Detune spread between voices.
 */
@KlangScript.Function
fun PatternMapperFn.sndSuperRamp(voices: PatternLike? = null, spread: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.sndSuperRamp(voices, spread, callInfo) }
